import { z } from 'zod';
import { PolicyEscalationType } from '../../models/policy';
import { getSmsMetrics } from '../../utils/smsUtils';
import { assistantProposalSchema } from './assistantProposal';
export const ASSISTANT_MAX_SUGGESTIONS = 6;
/** Most invoices / contacts one suggestion may act on */
export const ASSISTANT_MAX_SUGGESTION_TARGETS = 25;
/** A suggested SMS must fit one segment, so a send to N customers is billed as N messages */
export function assistantSmsBodyError(body) {
    var _a;
    const metrics = getSmsMetrics((_a = body === null || body === void 0 ? void 0 : body.trim()) !== null && _a !== void 0 ? _a : ``);
    if (!metrics.units) {
        return `smsBody is empty`;
    }
    return metrics.parts > 1
        ? `smsBody is ${metrics.units} GSM-7 characters; it must fit one SMS of at most ${metrics.singlePartLimit}`
        : null;
}
export const assistantSuggestionPriorities = [`high`, `medium`, `low`];
/**
 * Ad hoc actions the dashboard can run from a suggestion. Each maps onto an existing
 * on-demand send path, so the model never composes an email: reminders and statements use
 * the org's system policies, SMS takes the body the model drafts.
 */
export const assistantSuggestionActionTypes = [`send_invoice_reminders`, `send_statements`, `send_sms`, `raise_escalations`];
/**
 * One flat shape (strict structured output rejects oneOf): `actionType` says which of the id
 * lists and extras apply, `proposal` carries a policy change instead. Both null means the
 * suggestion is advice only.
 */
export const assistantSuggestionSchema = z.object({
    title: z.string().describe(`Short imperative headline, for example "Chase 5 customers who are 60+ days overdue".`),
    detail: z.string().describe(`Why this matters and what will happen, in light Markdown (paragraphs, - bullets, **bold**). Name customers and invoices, never contact details.`),
    priority: z.enum(assistantSuggestionPriorities),
    actionType: z.enum(assistantSuggestionActionTypes).nullable().describe(`The ad hoc action to run when the user clicks Do it. Null for advice or a policy proposal.`),
    invoiceIds: z.array(z.string()).describe(`send_invoice_reminders: invoice ids to remind. Otherwise empty.`),
    contactIds: z.array(z.number().int()).describe(`send_statements, send_sms, raise_escalations: contact ids. Otherwise empty.`),
    smsBody: z.string().nullable().describe(`send_sms only: the message for its single contact, under 160 GSM characters, no personal details. Otherwise null.`),
    escalationType: z.enum(Object.values(PolicyEscalationType)).nullable().describe(`raise_escalations only. Otherwise null.`),
    proposal: assistantProposalSchema.nullable().describe(`A policy change to apply, same rules as an assistant proposal. Null unless this suggestion is a configuration change.`),
    assistantPrompt: z.string().describe(`A message the user can send to the assistant chat to discuss or carry out this suggestion, written in the user's voice.`),
}).refine(suggestion => suggestion.actionType === null || suggestion.proposal === null, {
    message: `a suggestion has an action or a proposal, not both`,
}).refine(suggestion => suggestion.actionType !== `send_invoice_reminders` || suggestion.invoiceIds.length > 0, {
    message: `send_invoice_reminders needs invoiceIds`,
}).refine(suggestion => { var _a; return ![`send_statements`, `send_sms`, `raise_escalations`].includes((_a = suggestion.actionType) !== null && _a !== void 0 ? _a : ``) || suggestion.contactIds.length > 0; }, {
    message: `contact actions need contactIds`,
}).refine(suggestion => suggestion.actionType !== `send_sms` || new Set(suggestion.contactIds).size === 1, {
    message: `send_sms targets exactly one contact`,
}).refine(suggestion => { var _a; return suggestion.actionType !== `send_sms` || !!((_a = suggestion.smsBody) === null || _a === void 0 ? void 0 : _a.trim()); }, {
    message: `send_sms needs smsBody`,
}).refine(suggestion => suggestion.actionType !== `raise_escalations` || suggestion.escalationType !== null, {
    message: `raise_escalations needs escalationType`,
});
export const assistantSuggestionsReplySchema = z.object({
    summary: z.string().describe(`Two or three sentences on the overall state of the receivables, light Markdown.`),
    suggestions: z.array(assistantSuggestionSchema).describe(`At most ${ASSISTANT_MAX_SUGGESTIONS}, highest priority first. Empty when nothing needs doing.`),
});
//# sourceMappingURL=assistantSuggestion.js.map